import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Skill } from '../types';
import { MOTION_GRAPHICS_SCENE_DESIGN_ID } from 'js/src/agent/tools/motion-graphics/motionGraphicsSceneDesign';
import Flags from 'js/src/libs/flags';

/**
 * For a recipe to place where it decides how to show figures. A rule rather than a suggestion: a
 * one-line pointer was ignored in a test run, losing to the recipe's own stock-design options.
 * Empty without the flag.
 */
export function chartScenesPointer(indent: string): string {
    return Flags.canUseChartSubagent() ? `\n${indent}${CHART_SCENES_POINTER}` : '';
}

const CHART_SCENES_POINTER = `**A set of figures the viewer should compare** — a trend over time, a ranking, parts of a whole — **is a chart scene, not a stock design or a list of numbers.** When the source has one, at least one scene must be a chart: load \`${SKILL_NAMES.chartScenes}\` before adding it.`;

/**
 * A recipe as read by an agent that isn't offered `chart-scenes` (a subagent, which can't start the
 * chart subagent): the pointer would make it require a chart scene it can't make.
 */
export function withoutChartScenesPointer<T extends { instructions: string }>(skill: T): T {
    return {
        ...skill,
        get instructions() {
            return skill.instructions.replace(
                new RegExp(`\\n[ \\t]*${escapeRegExp(CHART_SCENES_POINTER)}`),
                '',
            );
        },
    };
}

function escapeRegExp(text: string): string {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export const chartScenesSkill: Skill = {
    name: SKILL_NAMES.chartScenes,
    description:
        'Animated chart scenes — bars, lines, comparisons, counters — designed by a chart subagent ' +
        'to match the video. Load when the content has numbers worth showing (figures, a table, ' +
        'a CSV, a stat-heavy document) or the user asks for a chart.',
    kind: 'howto',
    surfaces: ['sketch'],
    get instructions(): string {
        // Only an agent that authors motion graphics itself can be building a video whose design
        // system a delegated chart would break away from.
        const ownMotionGraphicsVideo = Flags.canUseCodeo()
            ? `

**Except in a motion graphics video you are building yourself** — one that has a design system:
author its charts like every other graphic, so they stay part of its one continuous design. This
skill is for a chart in any other video.`
            : '';
        return `# Chart Scenes

A chart scene is an animated motion graphic built from the numbers in the user's content. You add
the scene empty; a chart subagent designs it whole — the chart and its title — to fit the rest of
the video. **You don't design it yourself.**${ownMotionGraphicsVideo}

## When one earns a scene
Numbers the viewer should compare or follow: a change over time, a ranking, parts of a whole, a
before and after, a headline figure. One or two chart scenes in a video, on its strongest
numbers; not a chart for every figure. A single number said in passing is fine as text in a stock
design.

## The build
1. **Add the scene.** \`${TOOL_NAMES.addScene}\` with \`scene_design_id\` \`"${MOTION_GRAPHICS_SCENE_DESIGN_ID}"\`
   for a scene of its own, or \`${TOOL_NAMES.addVoiceoverScene}\` with the same id over the words
   that state the numbers in a voiceover. It shows nothing until it is designed. A scene of its own
   then needs its length, which the chart is paced to: set it with \`${TOOL_NAMES.setSceneDuration}\`
   before handing it over. One over a voiceover lasts as long as its words.
2. **Hand it to the subagent.** \`${TOOL_NAMES.runSubagent}\` with \`type\` \`chart\` and
   \`scene_ids\` set to the new scene's scene_id. Several chart scenes can be designed in parallel,
   one call each in the same message, even over the same voiceover. It sees neither the video nor
   your documents, only its \`prompt\` — plus the video's palette and fonts, how the scenes either
   side look and the voiceover over its scene, which it is given itself. So the \`prompt\` says:
   - what the scene says, in a sentence: "revenue doubled in two years, led by EMEA";
   - the figures, exactly — values, labels, units, time periods, pasted in, so the chart is
     accurate;
   - anything the user asked for ("compare 2024 to 2025"), with any wording or figures they want
     on screen.
   It chooses the title, which figures to label and what else goes on screen, keeping it to what
   a viewer can read in passing, so don't write its copy or ask for every value to be shown.
3. **Check it.** Review the scene the way you review any other. To change it — the user asks for
   different colors, a figure was wrong — start a chart subagent on the same scene again, saying
   what to change. It revises the existing design; never edit a chart's HTML yourself.

## When the video's frame changes
A chart scene keeps its design through an aspect ratio switch, still laid out for the old frame.
Start a chart subagent on each, all in the same message, asking it to lay the chart out again for
the new frame — give both frame sizes — keeping its data, copy, colours, type and the order and
pacing of its reveal.`;
    },
};
